import React, { Fragment, useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import {
    getAssessmentSummary,
    getPrescription,
    getPrescriptionNotes,
    isPPFilled,
} from '../../store/selectors/prescriptionPad';
import { fetchAndCacheAssessmentSummary } from '../../store/actions/app';
import {
    AssessmentSummary as AS,
    AssessmentSummaryEntity,
    AssessmentSummarySA,
    AtOrMediaEntity,
    MH_FA,
    MH_FH,
    MH_LH,
    MH_MC,
    MH_PP,
    QNA,
    SA,
} from '../../api/fetchAssessmentSummary';
import { GO_TO_PRESCRIPTION_PAD, PPRouterProps } from '../../routes/paths';

import openVC from '../videoRef';
import AppLoader from '../../components/common/AppLoader';
import {
    updatePPNotes,
    updateVitals,
    upsertDiagnose,
    upsertLabVital,
    upsertMedicalHistory,
    upsertSymptom,
} from '../../store/actions/prescriptionPad';
import { CHANNEL_SA, DIAGNOSIS, SELECT, SYMPTOM, track, VITAL } from '../../tracking';
import { AnimatePresence, motion } from 'framer-motion';
import { AntTooltip } from '../../components/common/AntTooltip';
import { MEDICAL_HISTORY_CATEGORY } from '../../store/types/medicalHistory';
import MediaViewer from '../../components/common/MediaViewer';
import { LOCAL_ID_PREFIX } from '../../constants';
import { randomString } from '../../store/utils';
import AntButton from '../../components/prescriptionPad/rightPanel/workspace/padV2/AntButton';
import { PRESCRIPTION_NOTES_EDITOR } from '../../components/common/PPNotesCard';
import { SERVER_VITALS } from '../../components/prescriptionPad/leftPanel/vitals';
import { VitalsEntity } from '../../store/types/prescription';

const AssessmentSummaryPPRightPanel: React.FC<{ showBottomPanel?: boolean }> = ({
    showBottomPanel = true,
}) => {
    const dispatch = useDispatch();
    const px = useSelector(getPrescription);
    const assessmentSummary = useSelector(getAssessmentSummary);
    const [isSymptomsFilled, setIsSymptomsFilled] = useState(false);
    const [isDiagonisFilled, setIsDiagnosisFilled] = useState(false);
    const [isMHCopied, setIsMHCopied] = useState(false);
    const [isVitalsCopied, setIsVitalsCopied] = useState(false);

    if (!assessmentSummary || !px) {
        return null;
    }

    if (assessmentSummary.fetchState === 'loading') {
        return (
            <div className="col-span-10 overflow-hidden pad-selected-shadow select-none w-full h-full">
                <div className="flex flex-col h-full overflow-hidden bg-neutral-9">
                    <div className="flex flex-col flex-auto mx-16 mt-16 overflow-auto bg-white justify-center items-center px-100 space-y-16">
                        <AppLoader />
                    </div>
                    <BottomPanel />
                </div>
            </div>
        );
    }

    if (assessmentSummary.fetchState === 'error') {
        return (
            <div className="col-span-10 overflow-hidden pad-selected-shadow select-none w-full h-full">
                <div className="flex flex-col h-full overflow-hidden bg-neutral-9">
                    <AssessmentSummaryError
                        onTryAgain={() =>
                            px && dispatch(fetchAndCacheAssessmentSummary({ visitId: px.visitId }))
                        }
                    />
                    <BottomPanel />
                </div>
            </div>
        );
    }

    const onSymptomsCopy = () => {
        const present = filterPresentingSymptoms(assessmentSummary);

        batch(() => {
            present.forEach(({ mdbid, mdbname, properties }) => {
                track(SELECT, { element: SYMPTOM, channel: CHANNEL_SA });
                dispatch(upsertSymptom({ id: mdbid, name: mdbname, properties }));
            });
        });

        setIsSymptomsFilled(true);
        return;
    };

    const onVitalsCopy = () => {
        if (!assessmentSummary || !assessmentSummary.sa?.vitals?.length) {
            return;
        }
        const vitals = assessmentSummary.sa?.vitals;

        batch(() => {
            vitals
                .filter((t) => t.value?.toString() && t.name)
                .forEach(({ id, name, unit, value, unit_id }) => {
                    track(SELECT, { element: VITAL, channel: CHANNEL_SA });
                    const serverVital = SERVER_VITALS.find((v) => v.id === id);
                    if (serverVital) {
                        const safe = serverVital.units_with_ranges.find(
                            (t) => t.code_id === unit_id,
                        )?.safe;
                        dispatch(
                            updateVitals({
                                name,
                                dis_name: name,
                                id,
                                value: {
                                    qt: value.toString(),
                                    unit,
                                    code_id: unit_id,
                                    safe: safe as VitalsEntity['value']['safe'],
                                },
                                date: new Date().toISOString(),
                                isoDate: new Date().toISOString(),
                            }),
                        );
                    } else {
                        dispatch(
                            upsertLabVital({
                                id,
                                name,
                                unit_dislay_name: unit,
                                value: value.toString(),
                                date: new Date().toISOString(),
                                unit: {
                                    name: unit,
                                    id: unit_id,
                                },
                                all_units: [
                                    {
                                        name: unit,
                                        id: unit_id,
                                    },
                                ],
                            }),
                        );
                    }
                });
        });

        setIsVitalsCopied(true);
        return;
    };

    const onDiagnosisCopy = () => {
        const present = filterDiagonsis(assessmentSummary);

        batch(() => {
            present.forEach(({ mdbid, mdbname, properties }) => {
                track(SELECT, { element: DIAGNOSIS, channel: CHANNEL_SA });
                dispatch(upsertDiagnose({ id: mdbid, name: mdbname, properties }));
            });
        });

        setIsDiagnosisFilled(true);
        return;
    };

    const onMHCopy = () => {
        const mh = filterMedicalHistory(assessmentSummary);

        batch(() => {
            mh.forEach((item: AssessmentSummaryEntity) => {
                const { mdbid, mdbname, type, meta } = item;
                if (type) {
                    track(SELECT, { element: 'mh', channel: CHANNEL_SA, type });

                    switch (type) {
                        case MH_LH:
                            dispatch(
                                upsertMedicalHistory(MEDICAL_HISTORY_CATEGORY['LIFESTYLE_HABITS'], {
                                    id: mdbid,
                                    name: mdbname,
                                    status: 'Active',
                                }),
                            );
                            break;

                        case MH_MC:
                            dispatch(
                                upsertMedicalHistory(
                                    MEDICAL_HISTORY_CATEGORY['PATIENT_MEDICAL_CONDITIONS'],
                                    {
                                        id: mdbid,
                                        name: mdbname,
                                        status: 'Active',
                                    },
                                ),
                            );
                            break;
                        case MH_FA:
                            dispatch(
                                upsertMedicalHistory(
                                    MEDICAL_HISTORY_CATEGORY['FOOD_OTHER_ALLERGY'],
                                    {
                                        id: mdbid,
                                        name: mdbname,
                                        status: 'Active',
                                    },
                                ),
                            );
                            break;
                        case MH_FH:
                            if (meta.who) {
                                dispatch(
                                    upsertMedicalHistory(
                                        MEDICAL_HISTORY_CATEGORY['FAMILY_HISTORY'],
                                        {
                                            id: mdbid,
                                            name: mdbname,
                                            who: meta.who,
                                            status: 'Active',
                                        },
                                    ),
                                );
                            } else {
                                track('ERROR', { on: 'onMHCopy', what: 'fh: who not found' });
                            }
                            break;
                        case MH_PP:
                            if (meta.on) {
                                dispatch(
                                    upsertMedicalHistory(
                                        MEDICAL_HISTORY_CATEGORY['PAST_PROCEDURES'],
                                        {
                                            id: mdbid,
                                            name: mdbname,
                                            status: 'Active',

                                            reportedAt: meta.reportedAt,
                                            notes: meta.notes,
                                            on: meta.on,
                                        },
                                    ),
                                );
                            } else {
                                track('ERROR', { on: 'onMHCopy', what: 'pp: on not found' });
                            }
                            break;
                    }
                } else {
                    track('ERROR', { on: 'onMHCopy', what: 'type not found' });
                }
            });
        });

        setIsMHCopied(true);
        return;
    };

    return (
        <div className="col-span-10 overflow-hidden pad-selected-shadow h-full flex flex-col relative w-full h-full">
            <div className="flex flex-col h-full overflow-hidden bg-neutral-9">
                <AssessmentSummary
                    assessmentSummary={assessmentSummary}
                    onSymptomsCopy={onSymptomsCopy}
                    isSymptomsFilled={isSymptomsFilled}
                    onDiagnosisCopy={onDiagnosisCopy}
                    isDiagonisFilled={isDiagonisFilled}
                    onMHCopy={onMHCopy}
                    isMHCopied={isMHCopied}
                    onVitalsCopy={onVitalsCopy}
                    isVitalsCopied={isVitalsCopied}
                />
            </div>
            {showBottomPanel && <BottomPanel />}
        </div>
    );
};

export default AssessmentSummaryPPRightPanel;

export const AssessmentSummary: React.FC<{
    assessmentSummary: SA;
    gridCol?: number;
    isOnPad?: boolean;
    onSymptomsCopy?: () => void;
    isSymptomsFilled?: boolean;
    onDiagnosisCopy?: () => void;
    isDiagonisFilled?: boolean;
    onMHCopy?: () => void;
    isMHCopied?: boolean;
    onVitalsCopy?: () => void;
    isVitalsCopied?: boolean;
    handleCloseSaPopup?: () => void;
}> = ({
    assessmentSummary,
    gridCol = 2,
    isOnPad = false,
    onSymptomsCopy: onFill,
    isSymptomsFilled: isFilled,
    isDiagonisFilled,
    onDiagnosisCopy,
    onMHCopy,
    isMHCopied,
    onVitalsCopy,
    isVitalsCopied,
    handleCloseSaPopup,
}) => {
    const dispatch = useDispatch();
    const prescriptionNotes = useSelector(getPrescriptionNotes);
    if (
        (!assessmentSummary.sa?.sy || assessmentSummary.sa.sy.length === 0) &&
        (!assessmentSummary.sa?.vitals || assessmentSummary.sa.vitals.length === 0) &&
        (!assessmentSummary.sa?.riskScore || assessmentSummary.sa.riskScore.length === 0) &&
        (!assessmentSummary.sa?.dy || assessmentSummary.sa.dy.length === 0) &&
        (!assessmentSummary.sa?.mh || assessmentSummary.sa.mh.length === 0) &&
        (!assessmentSummary.sa?.notes || assessmentSummary.sa.notes.length === 0) &&
        (!assessmentSummary.qna || assessmentSummary.qna?.length === 0) &&
        (!assessmentSummary.media || assessmentSummary.media?.length === 0) &&
        (!assessmentSummary.vitals || Object.keys(assessmentSummary.vitals).length === 0)
    ) {
        return (
            <div className="flex flex-col flex-1 mx-16 mt-16 overflow-auto bg-white justify-center items-center px-100 select-none">
                <svg
                    width="51"
                    height="41"
                    viewBox="0 0 51 41"
                    className="fill-neutral-4"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M24.9355 17.8945L26.4351 19.0693C26.6521 18.8751 26.9354 18.75 27.25 18.75H34.75C35.375 18.75 36 19.375 36 20C36 20.7031 35.375 21.25 34.75 21.25H29.2186L38.5 28.5212V10C38.5 8.67188 37.3281 7.5 36 7.5C35.2969 7.5 34.75 6.95312 34.75 6.25C34.75 5.625 35.2969 5 36 5C38.7344 5 41 7.26562 41 10V30.4797L50.1406 37.6406C50.9999 38.2656 51.1562 39.4375 50.4531 40.2188C49.8281 41.0781 48.6562 41.2344 47.8749 40.5312L1.70306 4.35938C0.843685 3.73438 0.687435 2.5625 1.39056 1.78125C1.70306 1.3125 2.24993 1 2.87493 1C3.26556 1 3.65618 1.15625 3.96868 1.46875L11.6655 7.49854C12.5205 6.01153 14.1207 5 16 5C16.625 5 17.25 5.625 17.25 6.25C17.25 6.95312 16.625 7.5 16 7.5C14.9341 7.5 14.0477 8.17332 13.6811 9.07762L22.9214 16.3167L23.8125 15.3906C24.2813 14.9219 25.0625 14.9219 25.6094 15.3906C26.0781 15.8594 26.0781 16.6406 25.6094 17.1875L24.9355 17.8945ZM13.5 17.9516L11 15.993V35C11 37.8125 13.1875 40 16 40H36C37.4277 40 38.7277 39.4036 39.644 38.4333L37.6484 36.8698C37.2006 37.2638 36.6172 37.5 36 37.5H16C14.5938 37.5 13.5 36.4062 13.5 35V17.9516ZM24.75 28.75H27.2838L24.2403 26.3657C23.7995 26.5749 23.5 27.0374 23.5 27.5C23.5 28.2031 24.0469 28.75 24.75 28.75ZM18.5 25.625C17.4063 25.625 16.625 26.4844 16.625 27.5C16.625 28.5938 17.4063 29.375 18.5 29.375C19.5156 29.375 20.375 28.5938 20.375 27.5C20.375 26.4844 19.5156 25.625 18.5 25.625ZM19.75 10H32.25C32.875 10 33.5 9.45312 33.5 8.75C33.5 8.125 32.875 7.5 32.25 7.5H30.2969C30.6875 6.79688 31 5.9375 31 5C31 2.26562 28.7344 0 26 0C23.1875 0 21 2.26562 21 5C21 5.9375 21.2344 6.79688 21.625 7.5H19.75C19.0469 7.5 18.5 8.125 18.5 8.75C18.5 9.45312 19.0469 10 19.75 10ZM26 2.5C27.3281 2.5 28.5 3.67188 28.5 5C28.5 6.40625 27.3281 7.5 26 7.5C24.5938 7.5 23.5 6.40625 23.5 5C23.5 3.67188 24.5938 2.5 26 2.5Z"
                    />
                </svg>
                <p className="text-neutral-4 text-center text-16 font-400 mt-32">
                    <b>Health Assessment</b> is an AI enabled guided set of questions around
                    symptoms that are asked to the patients on eka care patient app. <br></br>{' '}
                    <br></br> You will see a <b>summary view</b> here if the patient takes the
                    Health Assessment.
                </p>
            </div>
        );
    }

    const [openIndex, setOpenIndex] = useState<undefined | number>();
    const [summaryTabVisible, setSummaryTabVisible] = useState(true);

    const onAttachmentClick = (item: AtOrMediaEntity) => {
        const t = assessmentSummary.media?.findIndex((m) => m.url === item?.url);
        setOpenIndex(t);
    };

    const [activeTab, setActiveTab] = useState<'summary' | 'qna'>('summary');

    useEffect(() => {
        if (
            (!assessmentSummary.sa?.sy || assessmentSummary.sa.sy.length === 0) &&
            (!assessmentSummary.sa?.vitals || assessmentSummary.sa.vitals.length === 0) &&
            (!assessmentSummary.sa?.riskScore || assessmentSummary.sa.riskScore.length === 0) &&
            (!assessmentSummary.sa?.dy || assessmentSummary.sa.dy.length === 0) &&
            (!assessmentSummary.sa?.mh || assessmentSummary.sa.mh.length === 0) &&
            (!assessmentSummary.sa?.notes || assessmentSummary.sa.notes.length === 0)
        ) {
            setSummaryTabVisible(false);
            setActiveTab('qna');
        }
    }, []);

    return (
        <div className="flex flex-col overflow-hidden bg-neutral-9 relative w-full h-full">
            <div className="px-16 pt-8 space-y-10 shadow-600 bg-white">
                <div className="flex items-center space-x-12">
                    {summaryTabVisible && (
                        <div
                            onClick={() => setActiveTab('summary')}
                            className={`sa-tabs body-1 bold ${
                                activeTab === 'summary' ? 'active' : ''
                            }`}
                        >
                            <div className="flex items-center justify-center w-16 h-16">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 448 512"
                                    className="w-16 h-16"
                                >
                                    <path d="M352 0v96h96L352 0zM320 96V0H144C117.5 0 96 21.49 96 48v320C96 394.5 117.5 416 144 416h256c26.51 0 48-21.49 48-48V128h-96C334.4 128 320 113.6 320 96zM328 512h-208C53.83 512 0 458.2 0 392v-272C0 106.8 10.75 96 24 96S48 106.8 48 120v272c0 39.7 32.3 72 72 72h208c13.25 0 24 10.75 24 24S341.3 512 328 512z"></path>
                                </svg>
                            </div>
                            <p>Summary</p>
                        </div>
                    )}
                    <div
                        onClick={() => setActiveTab('qna')}
                        className={`sa-tabs body-1 bold ${activeTab === 'qna' ? 'active' : ''}`}
                    >
                        <div className="flex items-center justify-center w-16 h-16">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                                className="w-16 h-16"
                            >
                                <path d="M88 48C101.3 48 112 58.75 112 72V120C112 133.3 101.3 144 88 144H40C26.75 144 16 133.3 16 120V72C16 58.75 26.75 48 40 48H88zM488 72C501.3 72 512 82.75 512 96C512 109.3 501.3 120 488 120H184C170.7 120 160 109.3 160 96C160 82.75 170.7 72 184 72H488zM488 232C501.3 232 512 242.7 512 256C512 269.3 501.3 280 488 280H184C170.7 280 160 269.3 160 256C160 242.7 170.7 232 184 232H488zM488 392C501.3 392 512 402.7 512 416C512 429.3 501.3 440 488 440H184C170.7 440 160 429.3 160 416C160 402.7 170.7 392 184 392H488zM16 232C16 218.7 26.75 208 40 208H88C101.3 208 112 218.7 112 232V280C112 293.3 101.3 304 88 304H40C26.75 304 16 293.3 16 280V232zM88 368C101.3 368 112 378.7 112 392V440C112 453.3 101.3 464 88 464H40C26.75 464 16 453.3 16 440V392C16 378.7 26.75 368 40 368H88z"></path>
                            </svg>
                        </div>
                        <p>Questionnaire</p>
                    </div>
                </div>
            </div>
            {summaryTabVisible && activeTab === 'summary' ? (
                <SummaryTab
                    data={assessmentSummary}
                    onSymptomsCopy={onFill}
                    isSymptomsCopied={isFilled}
                    onDiagnosisCopy={onDiagnosisCopy}
                    isDiagonisFilled={isDiagonisFilled}
                    setMediaOpenIndex={setOpenIndex}
                    isMHCopied={isMHCopied}
                    onMHCopy={onMHCopy}
                    isVitalsCopied={isVitalsCopied}
                    onVitalsCopy={onVitalsCopy}
                />
            ) : (
                <>
                    <div className="flex-1 overflow-auto px-24 py-16" id="qna-content">
                        <QnAList
                            i={'qna'}
                            list={assessmentSummary.qna || []}
                            onAttachmentClick={onAttachmentClick}
                        />
                    </div>
                    {isOnPad ? (
                        <div className="w-full flex justify-end items-center p-8">
                            <AntButton
                                type="primary"
                                onClick={() => {
                                    const qnaElement = document.getElementById('qna-content');
                                    if (qnaElement) {
                                        const textFromQuestionnaire = qnaElement.innerHTML.replace(
                                            / class="[^"]*"/g,
                                            '',
                                        );
                                        const parsedTextFromQuestionnaire = qnaElement.innerText;

                                        if (
                                            !textFromQuestionnaire ||
                                            !parsedTextFromQuestionnaire
                                        ) {
                                            return;
                                        }

                                        if (!prescriptionNotes) {
                                            dispatch(
                                                updatePPNotes(
                                                    textFromQuestionnaire &&
                                                        parsedTextFromQuestionnaire
                                                        ? {
                                                              id: `${LOCAL_ID_PREFIX}${randomString(
                                                                  4,
                                                              )}`,
                                                              text: textFromQuestionnaire,
                                                              parsedText: parsedTextFromQuestionnaire,
                                                          }
                                                        : undefined,
                                                ),
                                            );
                                        } else {
                                            dispatch(
                                                updatePPNotes({
                                                    id: prescriptionNotes.id,
                                                    text:
                                                        prescriptionNotes.text +
                                                        textFromQuestionnaire,
                                                    parsedText:
                                                        prescriptionNotes.parsedText +
                                                        parsedTextFromQuestionnaire,
                                                }),
                                            );
                                        }

                                        const myContainer = document.getElementById(
                                            PRESCRIPTION_NOTES_EDITOR,
                                        ) as HTMLDivElement;
                                        if (myContainer) {
                                            myContainer.scrollIntoView({
                                                behavior: 'smooth',
                                                block: 'center',
                                                inline: 'nearest',
                                            });
                                        }
                                        handleCloseSaPopup?.();
                                    }
                                }}
                            >
                                Copy to Notes
                            </AntButton>
                        </div>
                    ) : null}
                </>
            )}
            {openIndex !== undefined && assessmentSummary.media && (
                <div className="absolute inset-0 bg-white p-8">
                    <MediaViewer
                        files={assessmentSummary.media.map((m) => ({
                            asset_url: m.url,
                            file_type: m.contentType,
                        }))}
                        selectedIndex={openIndex}
                        closeMe={() => setOpenIndex(undefined)}
                    />
                </div>
            )}
        </div>
    );
};

const SummaryTab: React.FC<{
    data: SA;
    isSymptomsCopied?: boolean;
    onSymptomsCopy?: () => void;
    onDiagnosisCopy?: () => void;
    setMediaOpenIndex: (index: number) => void;
    isDiagonisFilled?: boolean;
    onMHCopy?: () => void;
    isMHCopied?: boolean;
    onVitalsCopy?: () => void;
    isVitalsCopied?: boolean;
}> = ({
    data,
    isSymptomsCopied,
    onSymptomsCopy,
    onDiagnosisCopy,
    isDiagonisFilled,
    setMediaOpenIndex,
    onMHCopy,
    isMHCopied,
    onVitalsCopy,
    isVitalsCopied,
}) => {
    return (
        <div className="py-16 px-24 flex flex-col flex-1 overflow-auto space-y-16">
            {data.sa?.riskScore && data.sa.riskScore?.length > 0 && (
                <div className="flex items-center justify-between px-16 py-8 bg-darwin-accent-diagnosis-purple-200 rounded-8">
                    {data.sa?.riskScore?.map((item) => {
                        return (
                            <div className="flex space-x-4 text-16">
                                <span className="font-700">{item.name}:</span>
                                <span>{item.score}</span>
                            </div>
                        );
                    })}
                </div>
            )}
            {data.sa?.dy && data.sa.dy?.length > 0 && (
                <div className="flex items-center justify-between px-16 py-8 bg-darwin-accent-diagnosis-purple-200 rounded-8">
                    <p className="text-darwin-neutral-1000 bold body-1">
                        Suspected Diagnosis:{' '}
                        {filterDiagonsis(data)
                            .map((d) => d.mdbname)
                            .join(', ')}
                    </p>
                    <div
                        onClick={onDiagnosisCopy}
                        className="flex items-center space-x-12 cursor-pointer"
                    >
                        <p className="text-darwin-neutral-1000 bold body-3">
                            {isDiagonisFilled ? 'Copied to Prescription' : 'Copy to Prescription'}
                        </p>
                        <div className="flex items-center justify-center w-16 h-16">
                            {isDiagonisFilled ? (
                                <svg className="w-16 h-16 fill-status-success" viewBox="0 0 24 24">
                                    <path d="M0.41,13.41L6,19L7.41,17.58L1.83,12M22.24,5.58L11.66,16.17L7.5,12L6.07,13.41L11.66,19L23.66,7M18,7L16.59,5.58L10.24,11.93L11.66,13.34L18,7Z"></path>
                                </svg>
                            ) : (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                    className="w-14 h-14 fill-darwin-neutral-1000"
                                >
                                    <path d="M384 96L384 0h-112c-26.51 0-48 21.49-48 48v288c0 26.51 21.49 48 48 48H464c26.51 0 48-21.49 48-48V128h-95.1C398.4 128 384 113.6 384 96zM416 0v96h96L416 0zM192 352V128h-144c-26.51 0-48 21.49-48 48v288c0 26.51 21.49 48 48 48h192c26.51 0 48-21.49 48-48L288 416h-32C220.7 416 192 387.3 192 352z"></path>
                                </svg>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <MediaPanel data={data.media || []} setOpenIndex={setMediaOpenIndex} />
            <div className="flex flex-col space-y-16 xl:space-y-0 xl:flex-row cols">
                <MedicalHistory
                    onCopy={onMHCopy}
                    isCopied={isMHCopied}
                    data={filterMedicalHistory(data)}
                    vitals={data.sa?.vitals}
                    isVitalsCopied={isVitalsCopied}
                    onVitalsCopy={onVitalsCopy}
                />
                <PresentingSymptoms
                    data={filterPresentingSymptoms(data)}
                    onCopy={onSymptomsCopy}
                    isCopied={isSymptomsCopied}
                />
                <AbsentUnaware data={filterAbsentAnaware(data)} />
            </div>
        </div>
    );
};

const MediaPanel: React.FC<{ data: AtOrMediaEntity[]; setOpenIndex: (index: number) => void }> = ({
    data,
    setOpenIndex,
}) => {
    if (data.length === 0) {
        return null;
    }

    return (
        <div className="bg-white pt-8 pb-16 px-16">
            <p className="heading-2 bold">Media from the patient</p>
            <div className="flex flex-wrap items-center -ml-12">
                {data.map((m, index) => (
                    <div
                        onClick={() => setOpenIndex(index)}
                        className="ml-12 mt-12 cursor-pointer w-96 h-100 flex flex-col items-center bg-background-inputBG rounded-8 p-8"
                    >
                        {m.contentType === 'PDF' ? (
                            <Fragment>
                                <div className="flex items-end justify-center w-28 h-32">
                                    <svg
                                        className="w-28 h-32 pdf-icon"
                                        role="img"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 384 512"
                                    >
                                        <path d="M184 208c0-4.406-3.594-8-8-8S168 203.6 168 208c0 2.062 .2969 23.31 9.141 50.25C179.1 249.6 184 226.2 184 208zM256 0v128h128L256 0zM80 422.4c0 9.656 10.47 11.97 14.38 6.375C99.27 421.9 108.8 408 120.1 388.6c-14.22 7.969-27.25 17.31-38.02 28.31C80.75 418.3 80 420.3 80 422.4zM224 128L224 0H48C21.49 0 0 21.49 0 48v416C0 490.5 21.49 512 48 512h288c26.51 0 48-21.49 48-48V160h-127.1C238.3 160 224 145.7 224 128zM292 312c24.26 0 44 19.74 44 44c0 24.67-18.94 44-43.13 44c-5.994 0-11.81-.9531-17.22-2.805c-20.06-6.758-38.38-15.96-54.55-27.39c-23.88 5.109-45.46 11.52-64.31 19.1c-14.43 26.31-27.63 46.15-36.37 58.41C112.1 457.8 100.8 464 87.94 464C65.92 464 48 446.1 48 424.1c0-11.92 3.74-21.82 11.18-29.51c16.18-16.52 37.37-30.99 63.02-43.05c11.75-22.83 21.94-46.04 30.33-69.14C136.2 242.4 136 208.4 136 208c0-22.05 17.95-40 40-40c22.06 0 40 17.95 40 40c0 24.1-7.227 55.75-8.938 62.63c-1.006 3.273-2.035 6.516-3.082 9.723c7.83 14.46 17.7 27.21 29.44 38.05C263.1 313.4 284.3 312.1 287.6 312H292zM156.5 354.6c17.98-6.5 36.13-11.44 52.92-15.19c-12.42-12.06-22.17-25.12-29.8-38.16C172.3 320.6 164.4 338.5 156.5 354.6zM292.9 368C299 368 304 363 304 356.9C304 349.4 298.6 344 292 344H288c-.3438 .0313-16.83 .9687-40.95 4.75c11.27 7 24.12 13.19 38.84 18.12C288 367.6 290.5 368 292.9 368z"></path>
                                    </svg>
                                </div>
                                <p className="text-neutral-4 text-11">{m.contentType} attachment</p>
                            </Fragment>
                        ) : m.contentType === 'AUD' ? (
                            <Fragment>
                                <div className="flex items-center justify-center w-80 h-56">
                                    <img
                                        src="/images/sa-audio.png"
                                        alt="lock"
                                        className="w-full h-full"
                                    />
                                </div>
                                <p className="text-neutral-4 text-11">{m.contentType} attachment</p>
                            </Fragment>
                        ) : m.contentType === 'VID' ? (
                            <Fragment>
                                <div className="flex items-end justify-center w-80 h-56">
                                    <svg
                                        className="w-20 h-20 play-icon"
                                        role="img"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"
                                    >
                                        <path d="M176 480C148.6 480 128 457.6 128 432v-352c0-25.38 20.4-47.98 48.01-47.98c8.686 0 17.35 2.352 25.02 7.031l288 176C503.3 223.8 512 239.3 512 256s-8.703 32.23-22.97 40.95l-288 176C193.4 477.6 184.7 480 176 480z"></path>
                                    </svg>
                                </div>
                                <p className="text-neutral-4 text-11">{m.contentType} attachment</p>
                            </Fragment>
                        ) : m.contentType === 'IMG' ? (
                            <Fragment>
                                <div className="flex items-end justify-center w-80 h-56">
                                    <img src={m.url} className="rounded-4 w-full h-full" />
                                </div>
                                <p className="text-neutral-4 text-11">{m.contentType} attachment</p>
                            </Fragment>
                        ) : null}
                    </div>
                ))}
            </div>
        </div>
    );
};

export const seggregateSa = (sa?: AS): AssessmentSummaryEntity[][] => {
    return (sa || [])
        .filter(
            (item) =>
                item.status === 'present' || item.status === 'absent' || item.status === 'unknown',
        )
        .reduce(
            (result, item) => {
                result[item.status === 'present' ? 0 : item.status === 'absent' ? 1 : 2].push(item);
                return result;
            },
            [
                [] as AssessmentSummaryEntity[],
                [] as AssessmentSummaryEntity[],
                [] as AssessmentSummaryEntity[],
            ],
        );
};

const AbsentUnaware: React.FC<{
    data: AssessmentSummaryEntity[];
    onCopy?: () => void;
    isCopied?: boolean;
}> = ({ data, onCopy, isCopied }) => {
    if (!data.length) {
        return null;
    }

    const [present, absent, unknown]: AssessmentSummaryEntity[][] = seggregateSa(data);

    return (
        <div className="flex flex-col w-full px-16 py-8 space-y-16 col bg-white rounded-4 xl:rounded-none">
            <p className="heading-2 bold">Absent/Unaware Symptoms &amp; Other Notes</p>
            <div className="p-8 bg-darwin-neutral-50 rounded-4 select-none text-darwin-neutral-500 bold body-3">
                You cannot copy these to the prescription
            </div>
            <div className="space-y-16">
                {[...present, ...absent, ...unknown].map((item, index) => (
                    <div key={item.mdbid} className="flex items-center justify-between">
                        <div className="flex items-center space-x-8 w-80p">
                            <div className="flex items-center justify-center w-16 h-16">
                                <svg
                                    width="12"
                                    height="16"
                                    viewBox="0 0 12 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M11.3203 7.84375H10.8281C9.63281 7.84375 9.02344 6.41406 9.86719 5.57031L10.2422 5.21875C10.5 4.96094 10.5 4.53906 10.2422 4.28125C9.96094 4 9.53906 4 9.28125 4.28125L8.92969 4.63281C8.08594 5.47656 6.65625 4.86719 6.65625 3.69531V3.17969C6.65625 2.80469 6.35156 2.5 6 2.5C5.625 2.5 5.32031 2.80469 5.32031 3.17969V3.67188C5.32031 4.86719 3.89062 5.47656 3.04688 4.63281L2.69531 4.25781C2.4375 4 2.01562 4 1.73438 4.25781C1.45312 4.53906 1.45312 4.96094 1.73438 5.21875L2.08594 5.57031C2.95312 6.41406 2.34375 7.84375 1.14844 7.84375H0.65625C0.28125 7.84375 0 8.14844 0 8.5C0 8.875 0.28125 9.17969 0.65625 9.17969H1.17188C2.34375 9.17969 2.95312 10.6094 2.10938 11.4531L1.73438 11.8047C1.47656 12.0625 1.47656 12.4844 1.73438 12.7656C1.875 12.8828 2.03906 12.9531 2.22656 12.9531C2.39062 12.9531 2.55469 12.8828 2.69531 12.7656L3.04688 12.3906C3.89062 11.5469 5.32031 12.1562 5.32031 13.3516V13.8438C5.32031 14.2188 5.625 14.5234 6 14.5234C6.35156 14.5234 6.65625 14.2188 6.65625 13.8438V13.3516C6.65625 12.1562 8.08594 11.5469 8.92969 12.3906L9.28125 12.7656C9.42188 12.8828 9.58594 12.9531 9.75 12.9531C9.9375 12.9531 10.1016 12.8828 10.2422 12.7656C10.5 12.4844 10.5 12.0625 10.2422 11.8047L9.86719 11.4531C9.02344 10.6094 9.63281 9.17969 10.8281 9.17969H11.3203C11.6953 9.17969 12 8.875 12 8.5C12 8.14844 11.6953 7.84375 11.3203 7.84375ZM5.25 8.875C4.61719 8.875 4.125 8.38281 4.125 7.75C4.125 7.14062 4.61719 6.625 5.25 6.625C5.85938 6.625 6.375 7.14062 6.375 7.75C6.375 8.38281 5.85938 8.875 5.25 8.875ZM7.125 10.1875C6.79688 10.1875 6.5625 9.95312 6.5625 9.625C6.5625 9.32031 6.79688 9.08594 7.125 9.08594C7.42969 9.08594 7.6875 9.34375 7.6875 9.64844C7.6875 9.95312 7.42969 10.1875 7.125 10.1875Z"
                                        fill="#8D95B5"
                                    ></path>
                                    <path d="M1.5 1L10.5 15" stroke="#505775"></path>
                                </svg>
                            </div>
                            <p className="text-darwin-neutral-1000 body-1 capitalize">
                                {item.mdbname}
                            </p>
                        </div>
                        <p className="caption-2 bold text-darwin-neutral-500">{item.status}</p>
                    </div>
                ))}
            </div>
        </div>
    );
};

const MedicalHistory: React.FC<{
    data: AssessmentSummaryEntity[];
    onCopy?: () => void;
    onVitalsCopy?: () => void;
    isCopied?: boolean;
    isVitalsCopied?: boolean;
    vitals?: AssessmentSummarySA['vitals'];
}> = ({ data, onCopy, isCopied, vitals, isVitalsCopied, onVitalsCopy }) => {
    if (data.length === 0 && vitals?.length === 0) {
        return null;
    }
    return (
        <div className="flex flex-col w-full h-full bg-white space-y-16 rounded-4 xl:rounded-none col">
            {vitals?.length ? (
                <div className="flex flex-col w-full px-16 py-8 space-y-16">
                    <p className="heading-2 bold">Vitals</p>
                    {onVitalsCopy && (
                        <div
                            onClick={onVitalsCopy}
                            className="flex items-center justify-between p-8 bg-darwin-accent-medical-history-orange-100 rounded-4 border border-darwin-accent-medical-history-orange-500 cursor-pointer"
                        >
                            <p className="text-darwin-accent-medical-history-orange-900 bold body-3">
                                {isVitalsCopied ? 'Copied to vitals' : 'Copy to vitals'}
                            </p>
                            <div className="flex items-center justify-center w-16 h-16">
                                {isVitalsCopied ? (
                                    <svg
                                        className="w-16 h-16 fill-status-success"
                                        viewBox="0 0 24 24"
                                    >
                                        <path d="M0.41,13.41L6,19L7.41,17.58L1.83,12M22.24,5.58L11.66,16.17L7.5,12L6.07,13.41L11.66,19L23.66,7M18,7L16.59,5.58L10.24,11.93L11.66,13.34L18,7Z"></path>
                                    </svg>
                                ) : (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"
                                        className="w-14 h-14 fill-darwin-accent-medical-history-orange-900"
                                    >
                                        <path d="M384 96L384 0h-112c-26.51 0-48 21.49-48 48v288c0 26.51 21.49 48 48 48H464c26.51 0 48-21.49 48-48V128h-95.1C398.4 128 384 113.6 384 96zM416 0v96h96L416 0zM192 352V128h-144c-26.51 0-48 21.49-48 48v288c0 26.51 21.49 48 48 48h192c26.51 0 48-21.49 48-48L288 416h-32C220.7 416 192 387.3 192 352z"></path>
                                    </svg>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="space-y-16">
                        {vitals.map((item, index) => (
                            <div key={item.id} className="flex items-center space-x-8">
                                <div className="flex items-center justify-center w-16 h-16">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"
                                        className="w-12 h-12 fill-darwin-neutral-500"
                                    >
                                        <path d="M384 96L384 0h-112c-26.51 0-48 21.49-48 48v288c0 26.51 21.49 48 48 48H464c26.51 0 48-21.49 48-48V128h-95.1C398.4 128 384 113.6 384 96zM416 0v96h96L416 0zM192 352V128h-144c-26.51 0-48 21.49-48 48v288c0 26.51 21.49 48 48 48h192c26.51 0 48-21.49 48-48L288 416h-32C220.7 416 192 387.3 192 352z"></path>
                                    </svg>
                                </div>
                                <p className="text-darwin-neutral-1000 body-1 capitalize">
                                    {item.name}: {item.value} {item.unit}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}
            {data.length ? (
                <div className="flex flex-col w-full px-16 py-8 space-y-16">
                    <p className="heading-2 bold">Medical History</p>
                    {onCopy && (
                        <div
                            onClick={onCopy}
                            className="flex items-center justify-between p-8 bg-darwin-accent-medical-history-orange-100 rounded-4 border border-darwin-accent-medical-history-orange-500 cursor-pointer"
                        >
                            <p className="text-darwin-accent-medical-history-orange-900 bold body-3">
                                {isCopied
                                    ? 'Copied to patient medical history'
                                    : 'Copy to patient medical history'}
                            </p>
                            <div className="flex items-center justify-center w-16 h-16">
                                {isCopied ? (
                                    <svg
                                        className="w-16 h-16 fill-status-success"
                                        viewBox="0 0 24 24"
                                    >
                                        <path d="M0.41,13.41L6,19L7.41,17.58L1.83,12M22.24,5.58L11.66,16.17L7.5,12L6.07,13.41L11.66,19L23.66,7M18,7L16.59,5.58L10.24,11.93L11.66,13.34L18,7Z"></path>
                                    </svg>
                                ) : (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"
                                        className="w-14 h-14 fill-darwin-accent-medical-history-orange-900"
                                    >
                                        <path d="M384 96L384 0h-112c-26.51 0-48 21.49-48 48v288c0 26.51 21.49 48 48 48H464c26.51 0 48-21.49 48-48V128h-95.1C398.4 128 384 113.6 384 96zM416 0v96h96L416 0zM192 352V128h-144c-26.51 0-48 21.49-48 48v288c0 26.51 21.49 48 48 48h192c26.51 0 48-21.49 48-48L288 416h-32C220.7 416 192 387.3 192 352z"></path>
                                    </svg>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="space-y-16">
                        {data.map((item, index) => (
                            <div key={item.mdbid} className="flex items-center space-x-8">
                                <div className="flex items-center justify-center w-16 h-16">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"
                                        className="w-12 h-12 fill-darwin-neutral-500"
                                    >
                                        <path d="M384 96L384 0h-112c-26.51 0-48 21.49-48 48v288c0 26.51 21.49 48 48 48H464c26.51 0 48-21.49 48-48V128h-95.1C398.4 128 384 113.6 384 96zM416 0v96h96L416 0zM192 352V128h-144c-26.51 0-48 21.49-48 48v288c0 26.51 21.49 48 48 48h192c26.51 0 48-21.49 48-48L288 416h-32C220.7 416 192 387.3 192 352z"></path>
                                    </svg>
                                </div>
                                <p className="text-darwin-neutral-1000 body-1 capitalize">
                                    {item.mdbname}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

const PresentingSymptoms: React.FC<{
    data: AssessmentSummaryEntity[];
    onCopy?: () => void;
    isCopied?: boolean;
}> = ({ data, onCopy, isCopied }) => {
    const [expandedIndex, setExpandedIndex] = useState(new Set<number>(data.map((_, i) => i)));

    if (data.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-col w-full px-16 py-8 space-y-16 col bg-white rounded-4 xl:rounded-none">
            <div className="flex items-center justify-between ">
                <p className="heading-2 bold">Presenting Complaints</p>
                <div className="flex items-center space-x-6">
                    <p className="bold body-3 text-darwin-neutral-900">Clinical Details</p>
                    <div
                        onClick={() => {
                            if (expandedIndex.size === data.length) {
                                setExpandedIndex(new Set<number>());
                            } else {
                                setExpandedIndex(new Set<number>(data.map((_, i) => i)));
                            }
                        }}
                        className="darwin-switch primary"
                    >
                        <input type="checkbox" checked={expandedIndex.size === data.length} />
                        <span className="slider"></span>
                    </div>
                </div>
            </div>
            {onCopy && (
                <div
                    onClick={onCopy}
                    className="flex items-center justify-between p-8 bg-darwin-accent-symptoms-blue-100 rounded-4 border border-darwin-accent-symptoms-blue-500 cursor-pointer"
                >
                    <p className="text-darwin-accent-symptoms-blue-800 bold body-3">
                        {isCopied ? 'Symptoms copied to prescription' : 'Copy to prescription'}
                    </p>
                    <div className="flex items-center justify-center w-16 h-16">
                        {isCopied ? (
                            <svg className="w-16 h-16 fill-status-success" viewBox="0 0 24 24">
                                <path d="M0.41,13.41L6,19L7.41,17.58L1.83,12M22.24,5.58L11.66,16.17L7.5,12L6.07,13.41L11.66,19L23.66,7M18,7L16.59,5.58L10.24,11.93L11.66,13.34L18,7Z"></path>
                            </svg>
                        ) : (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                                className="w-14 h-14 fill-darwin-accent-symptoms-blue-800"
                            >
                                <path d="M384 96L384 0h-112c-26.51 0-48 21.49-48 48v288c0 26.51 21.49 48 48 48H464c26.51 0 48-21.49 48-48V128h-95.1C398.4 128 384 113.6 384 96zM416 0v96h96L416 0zM192 352V128h-144c-26.51 0-48 21.49-48 48v288c0 26.51 21.49 48 48 48h192c26.51 0 48-21.49 48-48L288 416h-32C220.7 416 192 387.3 192 352z"></path>
                            </svg>
                        )}
                    </div>
                </div>
            )}
            <div className="space-y-16">
                {data.map((item, index) => (
                    <div className="space-y-6">
                        <div
                            onClick={() => {
                                if (!item.properties || Object.keys(item.properties).length === 0) {
                                    return;
                                }
                                if (expandedIndex.has(index)) {
                                    expandedIndex.delete(index);
                                } else {
                                    expandedIndex.add(index);
                                }
                                setExpandedIndex(new Set(expandedIndex));
                            }}
                            key={item.mdbid + '-item'}
                            className={`${
                                item.properties && Object.keys(item.properties).length > 0
                                    ? 'cursor-pointer'
                                    : ''
                            } flex items-center justify-between`}
                        >
                            <div className="flex items-center space-x-8 w-80p">
                                <div className="flex items-center justify-center w-16 h-16">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 576 512"
                                        className="w-14 h-14 fill-darwin-neutral-500"
                                    >
                                        <path d="M515.6 227.6h-21.55c-50.68 0-76.06-61.28-40.23-97.12l15.25-15.25c11.11-11.11 11.11-29.11 .001-40.22c-11.11-11.11-29.11-11.11-40.22 .001l-15.24 15.24c-35.84 35.84-97.12 10.46-97.12-40.23V28.44c0-15.72-12.72-28.44-28.45-28.44S259.6 12.72 259.6 28.44v21.55c0 50.68-61.28 76.06-97.12 40.23L147.2 74.97C136.1 63.86 118.1 63.86 106.1 74.97c-11.11 11.11-11.11 29.11 .001 40.22l15.25 15.25C158.1 166.3 132.7 227.6 81.99 227.6H60.45C44.72 227.6 32 240.3 32 256s12.72 28.44 28.45 28.44h21.55c50.68 0 76.06 61.28 40.23 97.12l-15.25 15.25c-11.11 11.11-11.11 29.11-.001 40.22c5.555 5.555 12.83 8.333 20.11 8.333c7.277 0 14.55-2.779 20.11-8.334l15.24-15.25c35.84-35.84 97.12-10.46 97.12 40.23v21.55c0 15.72 12.72 28.45 28.45 28.45s28.45-12.72 28.45-28.45v-21.55c0-50.68 61.28-76.06 97.12-40.23l15.24 15.25c5.557 5.555 12.83 8.334 20.11 8.334c7.279 0 14.56-2.778 20.11-8.333c11.11-11.11 11.11-29.11-.001-40.22l-15.25-15.25c-35.84-35.84-10.46-97.12 40.23-97.12h21.55C531.3 284.4 544 271.7 544 256S531.3 227.6 515.6 227.6zM256 272c-26.51 0-48-21.49-48-48s21.49-48 48-48s48 21.49 48 48S282.5 272 256 272zM336 328c-13.25 0-24-10.75-24-24c0-13.26 10.75-23.1 24-23.1s24 10.74 24 24C360 317.3 349.3 328 336 328z"></path>
                                    </svg>
                                </div>
                                <p className="text-darwin-neutral-1000 body-1 capitalize">
                                    {item.mdbname}
                                </p>
                            </div>
                            {item.properties && Object.keys(item.properties).length > 0 && (
                                <div className="flex items-center justify-center w-16 h-16 expand-collapse-icon">
                                    <AntTooltip
                                        placement="top"
                                        content={expandedIndex.has(index) ? 'Collapse' : 'Expand'}
                                    >
                                        {expandedIndex.has(index) ? (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 512 512"
                                                className="fill-darwin-neutral-900 cursor-pointer w-12 h-12"
                                            >
                                                <path d="M344 232C357.3 232 368 242.7 368 256C368 269.3 357.3 280 344 280H168C154.7 280 144 269.3 144 256C144 242.7 154.7 232 168 232H344zM512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256zM256 48C141.1 48 48 141.1 48 256C48 370.9 141.1 464 256 464C370.9 464 464 370.9 464 256C464 141.1 370.9 48 256 48z"></path>
                                            </svg>
                                        ) : (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 512 512"
                                                className="fill-darwin-neutral-900 cursor-pointer w-12 h-12"
                                            >
                                                <path d="M232 344V280H168C154.7 280 144 269.3 144 256C144 242.7 154.7 232 168 232H232V168C232 154.7 242.7 144 256 144C269.3 144 280 154.7 280 168V232H344C357.3 232 368 242.7 368 256C368 269.3 357.3 280 344 280H280V344C280 357.3 269.3 368 256 368C242.7 368 232 357.3 232 344zM512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256zM256 48C141.1 48 48 141.1 48 256C48 370.9 141.1 464 256 464C370.9 464 464 370.9 464 256C464 141.1 370.9 48 256 48z"></path>
                                            </svg>
                                        )}
                                    </AntTooltip>
                                </div>
                            )}
                        </div>
                        <AnimatePresence>
                            {item.properties &&
                                Object.keys(item.properties).length > 0 &&
                                expandedIndex.has(index) && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        key={item.mdbid + '-item-p'}
                                        className="py-12 px-10 bg-neutral-9 rounded-4 mx-16 mt-8"
                                    >
                                        <div className="border-l border-darwin-neutral-400 border-opacity-20  space-y-16 pl-8">
                                            {item.properties &&
                                                Object.keys(item.properties).map((key, index) => (
                                                    <div className="flex items-start expanded-sa-sym">
                                                        <div className="flex items-center w-50p">
                                                            <p className="name text-darwin-neutral-900 body-3">
                                                                {item.properties &&
                                                                    item.properties[key].name}
                                                                :
                                                            </p>
                                                        </div>
                                                        {item.properties && (
                                                            <div className="w-50p space-y-4">
                                                                {item.properties[
                                                                    key
                                                                ].selection?.map((t) => (
                                                                    <p className="text-darwin-neutral-900 body-3 pl-16">
                                                                        {t.value +
                                                                            ' ' +
                                                                            (t.unit || '')}
                                                                    </p>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                        </div>
                                    </motion.div>
                                )}
                        </AnimatePresence>
                    </div>
                ))}
            </div>
        </div>
    );
};

const QnAList: React.FC<{
    i: string;
    list: QNA[];
    onAttachmentClick: (item: AtOrMediaEntity) => void;
}> = ({ i, list, onAttachmentClick }) => {
    return (
        <Fragment>
            {list.map((item, ii) => renderQNAItem(`nqna-${i}-${ii}`, item, onAttachmentClick))}
        </Fragment>
    );
};

const BottomPanel: React.FC = () => {
    const history = useHistory();
    const params: PPRouterProps = useParams();

    const goToPx = () => {
        history.replace(GO_TO_PRESCRIPTION_PAD(params.pxId, params.pxState));
    };

    const pres = useSelector(getPrescription);
    const isFilled = useSelector(isPPFilled);

    const onVideoCall = () => {
        if (!pres) {
            return;
        }
        openVC(pres.patient.profile.personal.name, pres.visitId); // do i change this also, ask vishwesh, dont see this in product
    };

    return (
        <Fragment>
            <div className="w-full border-b border-neutral-7"></div>
            <div className="flex justify-end p-16 bg-white space-x-16">
                <button
                    onClick={onVideoCall}
                    className="secondary-btn px-12 py-8 btn--small flex space-x-8 "
                >
                    <div>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 576 512"
                            width="18"
                            height="18"
                            className="fill-actionPrimary"
                        >
                            <path d="M543.9 96c-6.2 0-12.5 1.8-18.2 5.7L416 171.6v-59.8c0-26.4-23.2-47.8-51.8-47.8H51.8C23.2 64 0 85.4 0 111.8v288.4C0 426.6 23.2 448 51.8 448h312.4c28.6 0 51.8-21.4 51.8-47.8v-59.8l109.6 69.9c5.7 4 12.1 5.7 18.2 5.7 16.6 0 32.1-13 32.1-31.5v-257c.1-18.5-15.4-31.5-32-31.5zM384 400.2c0 8.6-9.1 15.8-19.8 15.8H51.8c-10.7 0-19.8-7.2-19.8-15.8V111.8c0-8.6 9.1-15.8 19.8-15.8h312.4c10.7 0 19.8 7.2 19.8 15.8v288.4zm160-15.7l-1.2-1.3L416 302.4v-92.9L544 128v256.5z"></path>
                        </svg>
                    </div>
                    <span className="w-full text-center">Video Call</span>
                </button>
                <button onClick={goToPx} className="primary-btn  px-12 py-8 btn--small ">
                    <span className="w-full text-center">
                        {isFilled ? 'Go to Prescription' : 'Create Prescription'}
                    </span>
                </button>
            </div>
        </Fragment>
    );
};

export const AssessmentSummaryError: React.FC<{ onTryAgain: () => void }> = ({ onTryAgain }) => {
    return (
        <div className="flex flex-col flex-auto mx-16 mt-16 overflow-auto bg-white justify-center items-center px-100 space-y-16">
            <svg width="64" height="64" viewBox="0 0 384 512" className="fill-neutral-3">
                <path d="M369.9,97.9,286,14A48,48,0,0,0,252.1-.1H48A48.16,48.16,0,0,0,0,48V464a48,48,0,0,0,48,48H336a48,48,0,0,0,48-48V131.9A48.23,48.23,0,0,0,369.9,97.9Zm-22.6,22.7a15.73,15.73,0,0,1,4.2,7.4H256V32.5a15.73,15.73,0,0,1,7.4,4.2ZM336,480H48a16,16,0,0,1-16-16V48A16,16,0,0,1,48,32H224V136a23.94,23.94,0,0,0,24,24H352V464A16,16,0,0,1,336,480ZM262.71,369.05l-5.66,5.66a12,12,0,0,1-17,0L192,326.63l-48.08,48.08a12,12,0,0,1-17,0l-5.66-5.66a12,12,0,0,1,0-17L169.37,304l-48.08-48.08a12,12,0,0,1,0-17l5.66-5.66a12,12,0,0,1,17,0L192,281.37l48.08-48.08a12,12,0,0,1,17,0l5.66,5.66a12,12,0,0,1,0,17L214.63,304l48.08,48.08A12,12,0,0,1,262.71,369.05Z" />
            </svg>
            <p>Failed to load Assessment Summary. </p>
            <button className="primary-btn px-8 py-4" onClick={onTryAgain}>
                Try again
            </button>
        </div>
    );
};
export function filterPresentingSymptoms(data: SA): AssessmentSummaryEntity[] {
    return data.sa?.sy?.filter((item) => item.status === 'present') || [];
}

export function filterAbsentAnaware(data: SA): AssessmentSummaryEntity[] {
    return [
        ...(data.sa?.sy?.filter((item) => item.status === 'absent' || item.status === 'unknown') ||
            []),
        ...(data.sa?.notes || []),
    ];
}

export function filterDiagonsis(data: SA): AssessmentSummaryEntity[] {
    return data.sa?.dy || [];
}

export function filterMedicalHistory(data: SA): AssessmentSummaryEntity[] {
    return data.sa?.mh?.filter((item) => item.status === 'present') || [];
}

function renderQNAItem(
    i: string,
    item: QNA,
    onAttachmentClick: (item: AtOrMediaEntity) => void,
): JSX.Element {
    return (
        <div key={`qna-${i}`} className="py-8 px-16 bg-white">
            <p className="text-neutral-black font-700 text-13">
                <strong className="text-neutral-black font-700 text-13">{item.q}</strong>
            </p>
            {item.a && <p className="text-neutral-black text-13">{item.a}</p>}
            {item.at &&
                item.at.map((ma) => (
                    <div key={ma.url} onClick={() => onAttachmentClick(ma)}>
                        <span className="text-actionPrimary text-13 font-700 border-b-1 border-actionPrimary cursor-pointer">
                            {ma.contentType} attachment
                        </span>
                        {/* {item.at.url.split('/').pop()} */}
                    </div>
                ))}
            {item.qlist && (
                <div className="border-l border-seperator-dark">
                    <QnAList
                        i={i.toString()}
                        list={item.qlist}
                        onAttachmentClick={onAttachmentClick}
                    />
                </div>
            )}
        </div>
    );
}
